import { stringify } from 'qs'
import type { Observable } from 'rxjs'
import { catchError, map, throwError } from 'rxjs'
import type { IUser } from '../Model/index.js'
import type { AjaxResponse } from 'rxjs/ajax'
import sentryTraceHeaders, { tracedAjax } from './Sentry.js'
import { assertNotMarkup } from './markupGuard.js'

export * as AuthenticationRepository from './Authentication/AuthenticationRepository.js'
export * as OrderRepository from './Order/OrderRepository.js'
export * as OrganizationRepository from './Organization/OrganizationRepository.js'
export * as PrimerRepository from './Primer/PrimerRepository.js'
export * as ProjectRepository from './Project/ProjectRepository.js'
export * as InviteRepository from './Invite/InviteRepository.js'
export * as OtuRepository from './Otu/OtuRepository.js'
export * as ApplicationRepository from './Application/ApplicationRepository.js'
export * as ConnectivityRepository from './Application/ConnectivityRepository.js'
export * as BillingRepository from './Billing/BillingRepository.js'
export * as ChatRepository from './Chat/ChatRepository.js'

export const post = <B, R>(path: string, body: B, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[POST] ${path}`)
  console.log(body)
  console.log(user)
  console.groupEnd()

  return tracedAjax({
    url: path,
    body: body,
    withCredentials: true,
    method: 'post'
  }).pipe(
    map((response) => {
      if (response instanceof Error) {
        console.error('error on Ajax Post')
      }
      assertNotMarkup(path, response.status, response.xhr?.getResponseHeader('content-type'))
      return response.response as R
    })
  )
}

export const get = <P extends object, R>(path: string, params?: P, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[GET] ${path}`)
  console.log(params)
  console.log(user)
  console.groupEnd()
  let url = path
  if (params)
    url += `?${stringify({
      ...params,
      filters: 'filters' in params ? JSON.stringify(params.filters) : null
    })}`

  return tracedAjax({
    url,
    withCredentials: true,
    headers: sentryTraceHeaders()
  }).pipe(
    map((response) => {
      assertNotMarkup(path, response.status, response.xhr?.getResponseHeader('content-type'))
      return response.response as R
    })
  )
}

// Unguarded, here and in removeWithPayload/fileUpload: no caller reads the emission, and the endpoints
// behind them answer 204, so a markup guard would only add a way to fail.
export const removeETag = <R>(path: string, etag: string, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[REMOVE] ${path}`)
  console.log(user)
  console.groupEnd()
  return tracedAjax({
    url: path,
    withCredentials: true,
    method: 'delete',
    headers: {
      ETag: etag
    }
  }).pipe(
    map((request) => request.response as R)
  )
}

export const remove = <R>(path: string, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[REMOVE] ${path}`)
  console.log(user)
  console.groupEnd()
  return tracedAjax({
    url: path,
    withCredentials: true,
    method: 'delete'
  }).pipe(
    map((request) => {
      assertNotMarkup(path, request.status, request.xhr?.getResponseHeader('content-type'))
      return request.response as R
    })
  )
}

export const removeWithPayload = <B, R>(path: string, body: B, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[REMOVE] ${path}`)
  console.log(user)
  console.groupEnd()
  return tracedAjax({
    url: path,
    body: body,
    withCredentials: true,
    method: 'delete'
  }).pipe(
    map((request) => request.response as R)
  )
}

export const put = <B, R>(path: string, body: B, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[PUT] ${path}`)
  console.log(body)
  console.log(user)
  console.groupEnd()
  return tracedAjax({
    url: path,
    body: body,
    withCredentials: true,
    method: 'put'
  }).pipe(
    map((request) => {
      assertNotMarkup(path, request.status, request.xhr?.getResponseHeader('content-type'))
      return request.response as R
    })
  )
}

export const putETag = <B, R>(path: string, body: B, etag: string, user?: IUser): Observable<R> => {
  console.groupCollapsed(`[PUT] ${path}`)
  console.log(body)
  console.log(user)
  console.groupEnd()
  return tracedAjax({
    url: path,
    body: body,
    withCredentials: true,
    method: 'put',
    headers: {
      ETag: etag
    }
  }).pipe(
    map((request) => {
      assertNotMarkup(path, request.status, request.xhr?.getResponseHeader('content-type'))
      return request.response as R
    })
  )
}

export const fileUpload = <B, R>(
  path: string,
  body: B & {
    [key: string]: string | Blob
  },
  file: File,
  user?: IUser
): Observable<R> => {
  const formData = new FormData()
  formData.append('file', file)
  Object.keys(body).forEach((key) => {
    formData.append(key, body[key])
  })

  console.groupCollapsed(`[FILE UPLOAD] ${path}`)
  console.log(file)
  console.log(user)
  console.groupEnd()

  return tracedAjax({
    url: path,
    body: formData,
    withCredentials: true,
    method: 'post',
    headers: {
      ...{ headers: "Content-Type': 'application/octet-stream" }
    }
  }).pipe(
    map((request) => request.response as R)
  )
}

export const fileUploadWithProgress = <B, R>(
  path: string,
  body: B & {
    [key: string]: string | Blob
  }
): Observable<AjaxResponse<R>> => {
  const formData = new FormData()
  Object.keys(body).forEach((key) => {
    formData.append(key, body[key])
  })

  return tracedAjax({
    url: path,
    method: 'POST',
    headers: {
      ...{ headers: "Content-Type': 'application/octet-stream" }
    },
    includeUploadProgress: true,
    withCredentials: true,
    body: formData
  })
}

export const download = <B>(path: string, body: B, user?: IUser): Observable<Blob> => {
  console.groupCollapsed(`[POST] ${path}`)
  console.log(body)
  console.log(user)
  console.groupEnd()

  return tracedAjax({
    url: path,
    method: 'GET',
    responseType: 'blob',
    withCredentials: true
  }).pipe(
    map((response) => {
      if (!response.response) {
        throw new Error('Download failed.')
      }
      return response.response as Blob
    }),
    catchError((error) => {
      console.error('Error downloading file:', error)
      throw error
    })
  )
}

export const downloadGet = <P extends object>(path: string, params?: P): Observable<{ blob: Blob; filename?: string }> => {
  console.groupCollapsed(`[DOWNLOAD GET] ${path}`)
  console.log(params)
  console.groupEnd()

  let url = path
  if (params) {
    url += `?${stringify({
      ...params,
      // keep same convention as your normal `get()`
      filters: 'filters' in params ? JSON.stringify(params.filters) : undefined,
      columns: 'columns' in params ? JSON.stringify(params.columns) : undefined
    })}`
  }

  return tracedAjax<Blob>({
    url,
    method: 'GET',
    responseType: 'blob',
    withCredentials: true,
    headers: {
      ...sentryTraceHeaders(),
      Accept: 'text/csv'
    }
  }).pipe(
    map((response: AjaxResponse<Blob>) => {
      const blob = response.response
      if (!blob) throw new Error('Download failed.')

      // Try to parse filename from Content-Disposition
      const cd = response.xhr?.getResponseHeader('Content-Disposition') ?? ''
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const filename = match?.[1] ? decodeURIComponent(match[1]) : undefined

      return { blob, filename }
    }),
    catchError((error) => {
      console.error('Error downloading file:', error)
      return throwError(() => error)
    })
  )
}
